'use client';

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ProductCardProps } from '../types';
import { HeartIcon, ShoppingCartIcon } from '../icons';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { highlightTokens, getQueryTokens } from '../highlightTokens';
import { addToCart, shouldShowCartButton } from '@/lib/cart/cartActions';
import { flyToCart } from '@/lib/cart/flyToCart';
import { track } from '@/lib/analytics/track';
import OfferPopup from '../OfferPopup';
import { PriceDisplay } from './PriceDisplay';
import { resolveProductBadge } from '@/lib/productBadge';
import SyfakProductCard, { CHOOSE_OFFER_LABEL, ADDED_LABEL, UPDATE_CART_LABEL, UPDATED_LABEL, type StatusTag } from './SyfakProductCard';
import { isInBasket, rememberBasketQuantity, startingQuantity } from '@/lib/basketQuantity';
import { QuantityInput } from './QuantityInput';
import { ProductTagWrap, ProductTagStack, ProductTagStrip, ProductTagStickers } from './ProductTags';
import { getTagsLayout, getServerTagsLayout, subscribeTagsLayout } from '@/lib/tagsLayout';
import type { ProductTag } from '@/lib/productTags';
import { addToShopFavourites, removeFromShopFavourites, hideShopNoticeAfterCall, loadShopFavourites, isFavourited, isShopperLoggedIn, subscribeFavourites, favouritesVersion } from '@/lib/favorites';
import { quantityRule, stepUp, stepDown, snapQuantity, formatQuantity } from '@/lib/quantityRules';

// Button label for products that need a choice on the shop's page first
// (has_options — bazaar's cut). Same uppercase style as the other card labels.
const CHOOSE_OPTIONS_LABEL = 'ΕΠΙΛΟΓΗ ΚΟΠΗΣ';
// Button label for a collection set (collection_set — kostaboda): its pieces
// are bought one by one on the set's page.
const VIEW_COLLECTION_LABEL = 'ΔΕΙΤΕ ΤΗ ΣΥΛΛΟΓΗ';

export default function ProductCard({
  product,
  variant,
  index = 0,
  animate = true,
  highlightQuery,
  customerPrice,
  loadingCustomerPrice = false,
  customerPriceUnavailableLabel = 'Επικοινωνήστε για τιμή',
  searchId = null,
  source = 'search',
}: ProductCardProps) {
  const clientConfig = useClientConfig();
  // Where product links open. Default '_blank' (new tab). '_self' navigates the
  // current window — for the embedded widget that's the host page, which unloads
  // the widget/modal. rel is only meaningful for new-tab links.
  const linkTarget = clientConfig.ui.productLinkTarget ?? '_blank';
  const linkRel = linkTarget === '_blank' ? 'noopener noreferrer' : undefined;
  const queryTokens = useMemo(() => getQueryTokens(highlightQuery), [highlightQuery]);
  const highlightedTitle = useMemo(
    () => highlightTokens(product.title || '', queryTokens),
    [product.title, queryTokens]
  );
  const discountPercent = product.original_price && product.price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  // Relative fallback paths must load from our app, not the host page,
  // when the widget is embedded cross-origin.
  const fallbackImage = clientConfig.ui.fallbackImageUrl
    ? clientConfig.ui.fallbackImageUrl.startsWith('/')
      ? `${clientConfig.apiOrigin ?? ''}${clientConfig.ui.fallbackImageUrl}`
      : clientConfig.ui.fallbackImageUrl
    : undefined;
  const imageUrl = product.image_url || fallbackImage;

  // Favorites heart — shown unless the client explicitly disables it (syfak).
  // Heart shown unless the client turns it off — and, where the client asks for
  // it (ui.favoritesLoggedInSelector, bazaar), only to logged-in shoppers.
  const favoritesLoggedInSelector = clientConfig.ui.favoritesLoggedInSelector;
  const showFavorites =
    clientConfig.features?.favorites !== false &&
    (!favoritesLoggedInSelector || isShopperLoggedIn(favoritesLoggedInSelector));
  // A working heart only when the client names the shop's favourites function
  // (ui.favoritesFunction, bazaar: 'wishlist.add'); otherwise decorative as before.
  const favoritesFunction = clientConfig.ui.favoritesFunction;
  const favoritesRemoveFunction = clientConfig.ui.favoritesRemoveFunction;
  const favoritesIdsUrl = clientConfig.ui.favoritesIdsUrl;
  const favoritesHideShopNotice = clientConfig.ui.favoritesHideShopNotice;
  // Every card reads the one shared list, so a heart changed anywhere (or the
  // shop's ids arriving) re-renders them all.
  useSyncExternalStore(subscribeFavourites, favouritesVersion, favouritesVersion);
  const faved = isFavourited(product.id);
  // Hearts open already filled from the shop's ids URL (bazaar, 2026-10-12).
  // Only where the heart is shown — for bazaar that means logged in.
  useEffect(() => {
    if (showFavorites && favoritesFunction && favoritesIdsUrl) void loadShopFavourites(favoritesIdsUrl);
  }, [showFavorites, favoritesFunction, favoritesIdsUrl]);
  // Which note to show: 'added' / 'removed' after a click; 'already' when a
  // filled heart is clicked and the client has no remove (add-only heart).
  const [favNote, setFavNote] = useState<false | 'added' | 'removed' | 'already'>(false);
  const favNoteTimer = useRef<number | undefined>(undefined);
  const showFavNote = useCallback((note: 'added' | 'removed' | 'already') => {
    setFavNote(note);
    window.clearTimeout(favNoteTimer.current);
    favNoteTimer.current = window.setTimeout(() => setFavNote(false), 1600);
  }, []);
  // A short "pop" when the heart is added (motion-safe: skipped for shoppers
  // who ask their system for reduced motion).
  const [favPop, setFavPop] = useState(false);
  const handleFavourite = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!favoritesFunction) return;
      if (faved) {
        if (!favoritesRemoveFunction) {
          showFavNote('already');
        } else if (removeFromShopFavourites(product.id, favoritesRemoveFunction)) {
          showFavNote('removed');
        }
        return;
      }
      if (addToShopFavourites(product.id, favoritesFunction)) {
        if (favoritesHideShopNotice) hideShopNoticeAfterCall(favoritesHideShopNotice);
        showFavNote('added');
        setFavPop(true);
        window.setTimeout(() => setFavPop(false), 180);
      }
    },
    [favoritesFunction, favoritesRemoveFunction, favoritesHideShopNotice, product.id, faved, showFavNote],
  );
  const favoritesColor = clientConfig.ui.favoritesColor;
  const favButtonProps = {
    ...(favoritesFunction
      ? { onClick: handleFavourite, 'aria-pressed': faved }
      : {}),
    ...(favoritesColor ? { style: { '--fav-color': favoritesColor } as React.CSSProperties } : {}),
  };
  // Heart colour: the client's favoritesColor on hover / once added, else the
  // brand colour exactly as before.
  const heartColorClass = favoritesColor
    ? `${faved ? 'text-[var(--fav-color)]' : 'text-[#231F20]'} hover:text-[var(--fav-color)]`
    : `${faved ? 'text-brand' : 'text-[#231F20]'} hover:text-brand`;
  // The pop scales the ICON, never the button: a transform on the button made
  // it its own layer, and the photo below then painted over the note inside it
  // for the length of the pop (founder's screenshot, 2026-10-12).
  const heartIconClass = favoritesColor
    ? `w-6 h-6 motion-safe:transition-transform motion-safe:duration-150 ${favPop ? 'motion-safe:scale-125' : ''}`
    : 'w-6 h-6';
  // A working heart holds the note: keep the button (and so the note) above the
  // photo and its labels (z-10), but under the sticky results header (z-20).
  const heartButtonLayer = favoritesFunction ? ' relative z-[15]' : '';
  const favNoteEl = favoritesFunction && favNote ? (
    <span role="status" className="absolute right-0 top-[28px] z-30 whitespace-nowrap rounded-[4px] bg-[#1A1A1A] px-2 py-1 text-[10px] font-gotham font-medium text-white pointer-events-none">
      {favNote === 'already' ? 'Ήδη στα αγαπημένα' : favNote === 'removed' ? 'Αφαιρέθηκε από τα αγαπημένα' : 'Προστέθηκε στα αγαπημένα'}
    </span>
  ) : null;

  // Config-driven status badge (e.g. syfak availability). No config → no badge.
  // syfak reads the per-customer stock label from its pricing response.
  const badge = resolveProductBadge(
    clientConfig.ui.productBadge,
    product as unknown as Record<string, unknown>,
    customerPrice,
    loadingCustomerPrice,
  );

  const cartButtonVisible = shouldShowCartButton(clientConfig.cart);
  // Phones and tablets (2026-11-03, decision f70a653f): the quantity box and the
  // button below are a HOVER reveal, and Tailwind v4 puts every hover rule inside
  // @media (hover:hover) — so a touch screen never showed them, and a tap on the
  // card opened the product (kostaboda: 74 Android clicks, 0 adds from search).
  // Every `[@media(hover:none)]:` class in this file is the exact complement: it
  // applies only where the hover reveal can't. Desktop keeps the hover reveal;
  // the only desktop change is at narrow widths (1024–1279px windows, phone
  // simulators), where the revealed button used to be cut off. syfak never had
  // this: its own card (SyfakProductCard) shows its actions always, and the
  // narrow-button rules here are copied from it.
  // Optional resting colour for the card button (ui.cartButtonColor); the CSS
  // var is read by .bg-cart-button, which falls back to the brand colour.
  // Optional corner rounding (ui.cartButtonRadius, px); unset = square, as before.
  const cartButtonRadius = clientConfig.ui.cartButtonRadius;
  const cartButtonStyle =
    clientConfig.ui.cartButtonColor || cartButtonRadius
      ? ({
          ...(clientConfig.ui.cartButtonColor && { '--color-cart-button': clientConfig.ui.cartButtonColor }),
          ...(cartButtonRadius && { borderRadius: `${cartButtonRadius}px` }),
        } as React.CSSProperties)
      : undefined;

  // syfak "month-offer": when the resolved customer price carries an offerUrl,
  // this product is in an offer — show "ΕΠΙΛΕΞΕ ΠΡΟΣΦΟΡΑ" (opens the offer popup
  // that renders the host's own offer HTML) instead of a plain add-to-cart, and
  // hide the per-card quantity stepper (the popup carries its own).
  const offerUrl = customerPrice?.offerUrl ?? null;
  const isOffer = cartButtonVisible && !!offerUrl;
  const [offerOpen, setOfferOpen] = useState(false);

  // Products whose feed says a choice is needed on the shop's own page (bazaar:
  // the cut, 94 products). Adding them blind would be ignored by the shop while
  // our button showed ✓, so the button opens the product page instead and the
  // stepper is hidden. Absent field → false → unchanged card.
  // A collection set (kostaboda) has no price of its own — its pieces are bought
  // one by one on the set's page — so it takes the same path: no price, no
  // stepper, and the button opens that page. Absent field → unchanged card.
  const isCollectionSet = !!product.collection_set;
  const needsChoice = cartButtonVisible && !isOffer && (isCollectionSet || !!(product as { has_options?: boolean }).has_options);
  const choiceLabel = isCollectionSet ? VIEW_COLLECTION_LABEL : CHOOSE_OPTIONS_LABEL;
  // How the quantity box counts: kg steps for weighed products, a minimum order
  // where the feed sets one, otherwise the old 1 / +1 (lib/quantityRules.ts).
  const { sold_by_weight: soldByWeight, min_quantity: minQuantity } =
    product as { sold_by_weight?: boolean; min_quantity?: number };
  // The shop's own labels from its feed (bazaar's <tags>), only when the client
  // turns ui.showProductTags on. Off → nothing below renders, card unchanged.
  const showTags = clientConfig.ui.showProductTags === true;
  const productTags = (product as { tags?: ProductTag[] }).tags;
  // Grid only: labels on the photo (shipped) or in a fixed strip under it — the
  // second exists for the /demo/bazaar switcher (lib/tagsLayout.ts).
  const tagsLayout = useSyncExternalStore(subscribeTagsLayout, getTagsLayout, getServerTagsLayout);
  const tagsUnder = showTags && tagsLayout === 'under';
  const qtyRule = useMemo(
    () => quantityRule({ sold_by_weight: soldByWeight, min_quantity: minQuantity }),
    [soldByWeight, minQuantity],
  );
  // «0,2 kg» for weighed products; plain numbers everywhere else.
  const formatQty = useCallback(
    (q: number) => formatQuantity(q, qtyRule) + (qtyRule.unit ? ` ${qtyRule.unit}` : ''),
    [qtyRule],
  );

  // What is already in the host's basket (syfak only; undefined everywhere else,
  // which keeps the old behaviour). The box starts there and the add SETS the
  // basket to the box — see lib/basketQuantity.ts.
  // A product with a minimum (0,2 kg, or 2 pieces) starts there.
  const defaultQuantity = qtyRule.min !== 1 ? qtyRule.min : (clientConfig.cart?.defaultQuantity ?? 1);
  const liveBasketQty = customerPrice?.cartQuantity;
  const [quantity, setQuantity] = useState(() => startingQuantity(liveBasketQty, defaultQuantity));
  const [basketQty, setBasketQty] = useState<number | undefined>(liveBasketQty);
  // Once the shopper touches the stepper, a late-arriving price must not
  // overwrite their number.
  const qtyTouchedRef = useRef(false);
  useEffect(() => {
    if (liveBasketQty === undefined) return;
    setBasketQty(liveBasketQty);
    if (!qtyTouchedRef.current) setQuantity(startingQuantity(liveBasketQty, defaultQuantity));
  }, [liveBasketQty, defaultQuantity]);
  const inBasket = isInBasket(basketQty);
  const decrementQty = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      qtyTouchedRef.current = true;
      setQuantity((q) => stepDown(q, qtyRule));
    },
    [qtyRule],
  );
  const incrementQty = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      qtyTouchedRef.current = true;
      setQuantity((q) => stepUp(q, qtyRule));
    },
    [qtyRule],
  );
  // A quantity TYPED into the box, snapped to a valid one (a multiple of the
  // kg step, never below the minimum; whole numbers ≥ 1 for everything else).
  const setTypedQty = useCallback((n: number) => {
    qtyTouchedRef.current = true;
    setQuantity(snapQuantity(n, qtyRule));
  }, [qtyRule]);

  // Fire an analytics interaction beacon (fire-and-forget; never blocks UX).
  // visitor/session/apiOrigin/clientId come from the config; searchId + source
  // + position localise the event within the funnel.
  const fireInteraction = useCallback(
    (type: 'click' | 'add_to_cart', extra: Record<string, unknown> = {}) => {
      track(clientConfig.apiOrigin, clientConfig.clientId, {
        type,
        product_id: product.id,
        search_id: searchId ?? null,
        visitor_id: clientConfig.visitorId ?? null,
        session_id: clientConfig.sessionId ?? null,
        external_customer_id: clientConfig.externalCustomerId ?? null,
        position: index,
        source,
        ...extra,
      });
    },
    [clientConfig.apiOrigin, clientConfig.clientId, clientConfig.visitorId, clientConfig.sessionId, clientConfig.externalCustomerId, product.id, searchId, source, index],
  );

  // Product-link click. NO preventDefault — the navigation must proceed (esp.
  // the '_self' case that unloads the widget); sendBeacon survives the unload.
  const handleProductClick = useCallback(() => {
    fireInteraction('click');
  }, [fireInteraction]);

  // needsChoice products: the button opens the product page (where the shop
  // asks for the cut), the same way the card's own product links open.
  const handleChooseOptions = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      handleProductClick();
      if (linkTarget === '_self') window.location.assign(product.url);
      else window.open(product.url, linkTarget, 'noopener,noreferrer');
    },
    [handleProductClick, linkTarget, product.url],
  );

  // Success feedback (founder request 2026-07-27 — adds were visually silent):
  // the button briefly flips to "✓ Προστέθηκε" and, when the client configures
  // cart.flyToCartSelector, a ghost dot flies to the host's cart icon (the same
  // gesture the host's own listing pages use). feedbackStrategy 'none' opts out.
  const [cartAdded, setCartAdded] = useState(false);
  // Whether the last successful click UPDATED an existing basket line (→ «✓
  // Ενημερώθηκε») or added a new one (→ «✓ Προστέθηκε»).
  const [lastWasUpdate, setLastWasUpdate] = useState(false);
  const actionLabel = inBasket ? UPDATE_CART_LABEL : clientConfig.ui.strings.addToCart;
  const doneLabel = lastWasUpdate ? UPDATED_LABEL : ADDED_LABEL;
  // Per-client: a cart glyph beside the label (not on the offer button, which adds
  // nothing, and not once the button reads «✓ Προστέθηκε»).
  // ON by default (founder, 2026-10-01): every client shows the cart icon unless
  // its ui_config sets cartButtonIcon: false. syfak's card (SyfakProductCard) has
  // its own button and never reads this.
  const cartGlyphAllowed = clientConfig.ui.cartButtonIcon !== false && !isOffer && !needsChoice;
  const showCartIcon = cartGlyphAllowed && !cartAdded;

  const handleAddToCart = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // currentTarget is only valid synchronously — capture before the await.
      const buttonEl = e.currentTarget as HTMLElement;
      const wasInBasket = inBasket;
      const result = await addToCart(
        { productId: product.id, productUrl: product.url, quantity },
        clientConfig.cart,
      );
      if (!result.success) {
        console.warn(`[cart] add-to-cart failed (${result.strategy}):`, result.error);
      } else {
        if (basketQty !== undefined) {
          // The host now holds exactly `quantity` (its add SETS the line).
          setBasketQty(quantity);
          rememberBasketQuantity(customerPrice, quantity);
          qtyTouchedRef.current = false;
        }
        if (clientConfig.cart?.feedbackStrategy !== 'none') {
          flyToCart(buttonEl, clientConfig.cart?.flyToCartSelector);
          setLastWasUpdate(wasInBasket);
          setCartAdded(true);
          window.setTimeout(() => setCartAdded(false), 1600);
        }
      }
      // Log regardless of success — a failed add is a funnel signal too.
      fireInteraction('add_to_cart', { quantity, strategy: result.strategy, success: result.success });
    },
    [product.id, product.url, quantity, clientConfig.cart, fireInteraction, inBasket, basketQty, customerPrice],
  );

  // The offer popup's add runs through the HOST's own prosfores.add — the popup
  // reports it back so the add still lands in our analytics funnel.
  const handleOfferCartAdd = useCallback(
    (qty: number) => {
      fireInteraction('add_to_cart', { quantity: qty, strategy: 'prosfores_offer', success: true, offer: true });
    },
    [fireInteraction],
  );

  const handleOfferClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setOfferOpen(true);
      fireInteraction('click', { offer: true });
    },
    [fireInteraction],
  );

  // syfak B2B tailoring: live status tags from the pricing endpoint's `labels`,
  // mapped to Greek via ui.pricingLabels ('1' flag → tag; date → "label: value";
  // text → the value).
  const statusTags = useMemo(() => {
    const cfg = clientConfig.ui.pricingLabels;
    const labels = customerPrice?.labels;
    if (!cfg || !labels) return [] as StatusTag[];
    const out: StatusTag[] = [];
    for (const { key, label, type, tone } of cfg) {
      const raw = labels[key];
      if (raw == null) continue;
      const s = String(raw).trim();
      // `tone` is per-LABEL, not per-value: a tag's colour is a property of what
      // the status means, so it travels with the text rather than being matched
      // by string later (the date/text variants don't render `label` at all).
      if (type === 'date') { if (s && s !== '0') out.push({ text: `${label}: ${s}`, tone }); }
      else if (type === 'text') { if (s) out.push({ text: s, tone }); }
      else if (s === '1') out.push({ text: label, tone });
    }
    return out;
  }, [clientConfig.ui.pricingLabels, customerPrice?.labels]);

  // Config-gated: syfak uses the dedicated, always-visible B2B card for BOTH grid
  // and list. ProductCard owns the state/handlers; SyfakProductCard is layout-only.
  if (clientConfig.ui.alwaysShowActions) {
    return (
      <SyfakProductCard
        product={product}
        variant={variant}
        animate={animate}
        index={index}
        imageUrl={imageUrl}
        highlightedTitle={highlightedTitle}
        badge={badge}
        statusTags={statusTags}
        customerPrice={customerPrice}
        loadingCustomerPrice={loadingCustomerPrice}
        customerPriceUnavailableLabel={customerPriceUnavailableLabel}
        cartButtonVisible={cartButtonVisible}
        isOffer={isOffer}
        offerUrl={offerUrl}
        offerOpen={offerOpen}
        quantity={quantity}
        cartAdded={cartAdded}
        actionLabel={actionLabel}
        doneLabel={doneLabel}
        onProductClick={handleProductClick}
        onAddToCart={handleAddToCart}
        onOfferClick={handleOfferClick}
        onDecrement={decrementQty}
        onIncrement={incrementQty}
        onSetQuantity={setTypedQty}
        onOfferClose={() => setOfferOpen(false)}
        onOfferCartAdd={handleOfferCartAdd}
      />
    );
  }

  if (variant === 'list') {
    return (
      <div
        className={`group flex items-start min-h-[116px] w-full bg-white border-b-[0.5px] border-[#D8D8D8] ${animate ? 'product-enter' : ''}`}
        data-product-id={product.id}
        style={animate ? { animationDelay: `${Math.min(index * 30, 600)}ms` } : undefined}
      >
        {/* Left: Offer badge + image */}
        <div className="flex flex-col items-center w-[96px] self-stretch flex-shrink-0">
          <div className="flex items-center px-2 py-3 h-[30px]">
            {discountPercent && discountPercent > 0 ? (
              <span className="bg-[#D0202B] text-white text-[12px] font-gotham font-bold leading-none px-[6px] py-[5px] rounded-[4px] tabular-nums">
                -{discountPercent}%
              </span>
            ) : (
              <span />
            )}
          </div>
          <a href={product.url} target={linkTarget} rel={linkRel} onClick={handleProductClick} className="flex items-center justify-center px-[10px]">
            {imageUrl ? (
              <span className="relative flex-shrink-0">
                <img
                  src={imageUrl}
                  alt={product.title || 'Product'}
                  className="w-[78px] h-[78px] flex-shrink-0 aspect-square object-contain"
                />
                {showTags && <ProductTagStickers tags={productTags} size={30} />}
              </span>
            ) : (
              <div className="w-[78px] h-[78px] flex-shrink-0 aspect-square flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </a>
        </div>

        {/* Right: Product info (@container: the touch button below goes
            icon-only when this column is too narrow for stepper + label) */}
        <div className="@container relative flex-1 min-w-0 flex flex-col gap-[6px] min-h-[116px] pt-3 px-4 border-l-[0.5px] border-[#D8D8D8]">
          {/* Availability badge — its own line ABOVE the brand (full text, so
              long labels like SYFAK availability don't squeeze the brand/title
              on narrow cards). Only for clients with a productBadge. */}
          {badge && (
            <span
              className="self-start max-w-full px-1.5 py-[2px] text-[9px] font-gotham font-bold leading-[12px]"
              style={{ backgroundColor: badge.background, color: badge.textColor }}
            >
              {badge.label}
            </span>
          )}
          {/* Brand (+ favourite) */}
          <div className="flex items-center justify-between self-stretch gap-2">
            <p className="text-[12px] font-gotham font-bold leading-normal min-w-0 truncate" title={product.brand || undefined} style={{ color: 'var(--color-product-title, var(--color-brand))' }}>
              {product.brand}
            </p>
            {showFavorites && (
              <button {...favButtonProps} className={`w-6 h-6 flex items-center justify-center ${heartColorClass} transition-colors cursor-pointer flex-shrink-0${heartButtonLayer}`} aria-label="Add to favorites">
                <HeartIcon className={heartIconClass} filled={faved} />
                {favNoteEl}
              </button>
            )}
          </div>
          {/* Title + price */}
          <div className="flex flex-col gap-[10px] self-stretch">
            <a
              href={product.url}
              target={linkTarget}
              rel={linkRel}
              onClick={handleProductClick}
              className="text-[12px] font-gotham font-normal text-black leading-normal line-clamp-2 hover:text-brand transition-colors cursor-pointer self-stretch"
            >
              {highlightedTitle}
            </a>
            {!isCollectionSet && (
              <PriceDisplay
                layout="horizontal"
                loading={loadingCustomerPrice}
                customerPrice={customerPrice}
                rrp={product.price ?? null}
                rrpOriginal={product.original_price ?? null}
                unavailableLabel={customerPriceUnavailableLabel}
                perUnit={qtyRule.unit === 'kg' ? 'κιλό' : undefined}
              />
            )}
          </div>
          {showTags && <ProductTagWrap tags={productTags} className="self-stretch mb-3" />}
          {/* Hover: quantity + add to cart — absolutely positioned bottom-right.
              Touch: always shown, in the flow under the price (absolute, it sat
              on top of the price on a phone), with finger-size − / +. Where the
              column can't fit stepper + label (every phone; a desktop window
              under ~640px, where it overlapped the price), the add button is a
              square basket, as on syfak's list card; the label stays for screen
              readers. */}
          {cartButtonVisible && (
            <div className="hidden group-hover:flex [@media(hover:none)]:flex [@media(hover:none)]:static [@media(hover:none)]:self-end [@media(hover:none)]:mt-auto [@media(hover:none)]:mb-3 items-center gap-[10px] absolute right-4 bottom-3">
              {!isOffer && !needsChoice && (
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={decrementQty}
                    aria-label="Decrease quantity"
                    className="flex items-center justify-center w-6 h-6 [@media(hover:none)]:w-8 [@media(hover:none)]:h-8 text-[16px] font-gotham font-normal text-black cursor-pointer disabled:opacity-40"
                    disabled={quantity <= qtyRule.min}
                  >−</button>
                  <QuantityInput value={quantity} onCommit={setTypedQty} decimals={qtyRule.decimals} format={qtyRule.unit ? formatQty : undefined} className={`${qtyRule.unit ? 'w-12' : 'w-6 [@media(hover:none)]:w-8'} h-6 [@media(hover:none)]:h-8 border border-[#E2E2E2] text-[10px] font-gotham font-medium text-black`} />
                  <button
                    type="button"
                    onClick={incrementQty}
                    aria-label="Increase quantity"
                    className="flex items-center justify-center w-6 h-6 [@media(hover:none)]:w-8 [@media(hover:none)]:h-8 text-[16px] font-gotham font-normal text-black cursor-pointer"
                  >+</button>
                </div>
              )}
              <button
                onClick={isOffer ? handleOfferClick : needsChoice ? handleChooseOptions : handleAddToCart}
                aria-label={cartGlyphAllowed ? (cartAdded ? doneLabel : actionLabel) : undefined}
                className={`flex items-center justify-center gap-[10px] p-[10px] [@media(hover:none)]:min-h-10${cartGlyphAllowed ? ' @max-[309px]:min-w-10' : ''} bg-cart-button hover:bg-brand-hover text-brand-text text-[12px] font-gotham font-medium transition-colors cursor-pointer`}
                style={cartButtonStyle}
              >
                {showCartIcon && <ShoppingCartIcon />}
                {cartGlyphAllowed && cartAdded && <span className="hidden @max-[309px]:inline" aria-hidden>✓</span>}
                <span className={cartGlyphAllowed ? '@max-[309px]:sr-only' : undefined}>{isOffer ? CHOOSE_OFFER_LABEL : needsChoice ? choiceLabel : cartAdded ? doneLabel : actionLabel}</span>
              </button>
            </div>
          )}
        </div>
        {offerOpen && offerUrl && (
          <OfferPopup offerUrl={offerUrl} title={product.title} onClose={() => setOfferOpen(false)} onCartAdd={handleOfferCartAdd} />
        )}
      </div>
    );
  }

  // Grid variant — matches Figma card (292x383; height +5px so the hover
  // price+qty+button stack fits under a 2-line clamped title)
  // Touch: price, stepper and button all show, so the fixed height gives way to
  // the grid row (rows stretch every card to the tallest one) and the price
  // block is pinned to the bottom, as on syfak's card — buttons line up across
  // a row, and a two-line button can never be cut off.
  return (
    <div
      className={`@container group bg-white flex flex-col items-start ${tagsUnder ? 'h-[458px]' : 'h-[388px]'}${cartButtonVisible ? ' [@media(hover:none)]:h-auto' : ''} overflow-hidden ${animate ? 'product-enter' : ''}`}
      data-product-id={product.id}
      style={animate ? { animationDelay: `${Math.min(index * 40, 600)}ms` } : undefined}
    >
      {/* Offer badge + Heart row — Figma: px-8 py-12, justify-between, h-24 */}
      <div className="flex items-center justify-between w-full px-2 py-3">
        <div className="flex flex-col h-6 items-center justify-center">
          {discountPercent && discountPercent > 0 ? (
            // Sale red, the SAME for every client (not the brand colour): red reads as
            // "offer" on any storefront. #D0202B carries white text at 5.35:1.
            // Founder's pick (option A, 2026-10-01): the badge carries the offer; the
            // price itself stays black.
            <span className="bg-[#D0202B] text-white text-[13px] font-gotham font-bold leading-none px-2 py-[5px] rounded-[4px] tabular-nums">
              -{discountPercent}%
            </span>
          ) : (
            <span />
          )}
        </div>
        <div className="flex items-center h-6">
          {showFavorites && (
            <button {...favButtonProps} className={`w-6 h-6 flex items-center justify-center ${heartColorClass} transition-colors cursor-pointer${heartButtonLayer}`} aria-label="Add to favorites">
              <HeartIcon className={heartIconClass} filled={faved} />
              {favNoteEl}
            </button>
          )}
        </div>
      </div>

      {/* Product image — Figma: 188x188 centered, px-10 */}
      <a href={product.url} target={linkTarget} rel={linkRel} onClick={handleProductClick} className="flex items-center justify-center w-full px-[10px] cursor-pointer">
        <div className="relative w-[188px] h-[188px]">
          {showTags && !tagsUnder && <ProductTagStack tags={productTags} />}
          {showTags && <ProductTagStickers tags={productTags} />}
          {badge && (
            <span
              className="absolute top-0 right-0 z-10 max-w-full px-1.5 py-[3px] text-[9px] font-gotham font-bold leading-[12px] text-right"
              style={{ backgroundColor: badge.background, color: badge.textColor }}
            >
              {badge.label}
            </span>
          )}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.title || 'Product'}
              className="w-full h-full object-contain transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>
      </a>

      {/* Labels under the photo: a fixed two-row strip (+70px on the card), and
          the title below always takes two lines — so every card is the same
          height and price + button land at the same place on every card. */}
      {tagsUnder && <ProductTagStrip tags={productTags} className="self-stretch mx-4 mt-2 mb-[6px]" />}

      {/* Product info — Figma: border-top 0.5px #D8D8D8, pt-12 px-16, gap-6 */}
      <div className="border-t-[0.5px] border-[#D8D8D8] pt-3 px-4 w-full flex flex-col gap-[6px] items-start">
        <p className="text-[12px] font-gotham font-bold leading-normal max-w-full truncate" title={product.brand || undefined} style={{ color: 'var(--color-product-title, var(--color-brand))' }}>
          {product.brand}
        </p>
        <a
          href={product.url}
          target={linkTarget}
          rel={linkRel}
          onClick={handleProductClick}
          className={`text-[12px] font-gotham font-normal text-black leading-normal line-clamp-2 hover:text-brand transition-colors cursor-pointer${tagsUnder ? ' h-[36px]' : ''}`}
        >
          {highlightedTitle}
        </a>
      </div>

      {/* Price — Initial state. Hidden on hover ONLY when there's a cart action
          to swap in; cart-less cards (e.g. syfak) stay static on hover.
          Touch keeps THIS price (the stepper row below then holds the stepper
          alone: price + stepper side by side don't fit a 148px phone card) and
          starts the bottom-pinned block. */}
      <div className={`flex items-center self-stretch px-4 gap-[91px]${cartButtonVisible ? ' group-hover:hidden [@media(hover:none)]:mt-auto [@media(hover:none)]:pt-1.5' : ''}`}>
        {!isCollectionSet && (
          <PriceDisplay
            layout="horizontal"
            loading={loadingCustomerPrice}
            customerPrice={customerPrice}
            rrp={product.price ?? null}
            rrpOriginal={product.original_price ?? null}
            unavailableLabel={customerPriceUnavailableLabel}
            perUnit={qtyRule.unit === 'kg' ? 'κιλό' : undefined}
          />
        )}
      </div>

      {/* Hover state — Price + Quantity row (only when a cart action exists).
          Touch: the stepper alone, finger-size, aligned with the price above;
          the row is left out where there is no stepper (it would be empty). */}
      {cartButtonVisible && (
      <div className={`hidden group-hover:flex${!isOffer && !needsChoice ? ' [@media(hover:none)]:flex' : ''} justify-between items-center self-stretch py-2 [@media(hover:hover)]:@max-[172px]:py-1.5 px-1`}>
        {!isCollectionSet && (
          <div className="[@media(hover:none)]:hidden">
            <PriceDisplay
              layout="stacked"
              loading={loadingCustomerPrice}
              customerPrice={customerPrice}
              rrp={product.price ?? null}
              rrpOriginal={product.original_price ?? null}
              unavailableLabel={customerPriceUnavailableLabel}
              perUnit={qtyRule.unit === 'kg' ? 'κιλό' : undefined}
            />
          </div>
        )}
        {/* Quantity selector — hidden for offers (the popup carries its own) */}
        {!isOffer && !needsChoice && (
          <div className="flex items-center">
            <button
              type="button"
              onClick={decrementQty}
              aria-label="Decrease quantity"
              className="flex flex-col justify-center items-center w-6 h-6 [@media(hover:none)]:w-8 [@media(hover:none)]:h-8 p-[10px] gap-[10px] text-[16px] font-gotham font-normal text-black cursor-pointer disabled:opacity-40"
              disabled={quantity <= qtyRule.min}
            >−</button>
            <QuantityInput value={quantity} onCommit={setTypedQty} decimals={qtyRule.decimals} format={qtyRule.unit ? formatQty : undefined} className={`${qtyRule.unit ? 'w-12' : 'w-6 [@media(hover:none)]:w-8'} h-6 [@media(hover:none)]:h-8 border border-[#E2E2E2] text-[10px] font-gotham font-medium text-black`} />
            <button
              type="button"
              onClick={incrementQty}
              aria-label="Increase quantity"
              className="flex flex-col justify-center items-center w-6 h-6 [@media(hover:none)]:w-8 [@media(hover:none)]:h-8 p-[10px] gap-[10px] text-[16px] font-gotham font-normal text-black cursor-pointer"
            >+</button>
          </div>
        )}
      </div>
      )}

      {/* Hover state — Add to cart button.
          Narrow button, any device (from syfak's card; founder's call there: the
          label alone reads better): the basket glyph drops when it would push
          the label onto a second line. «ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ» measures 152px, +28
          with the glyph, so below 181px of label room (@container = this
          button) it goes. That alone keeps the label on one line on a 440px
          phone and at 1152–1279px desktop widths; from 1280px the button is
          wide enough and nothing changes.
          Narrower still the label wraps. Touch: on a 158–175px card (iPhone
          12–16 and 16 Pro, 384–412px Androids) 11px + px-2 keeps it on one
          line (139px at 11px; founder's call 2026-11-03 — not below 11px, so
          the smallest phones, iPhone SE/mini and 360px Androids, keep two
          lines at full size). Below that the button GROWS (min-h-10, the card
          has no fixed height there). Desktop: the card's fixed height
          leaves 1px spare, so two lines were cut off at 1024–1151px — below a
          172px card (@container = the card) the button and the stepper row
          tighten by just enough that two lines fit. */}
      {cartButtonVisible && (
        <button
          onClick={isOffer ? handleOfferClick : needsChoice ? handleChooseOptions : handleAddToCart}
          className="@container hidden group-hover:flex [@media(hover:none)]:flex [@media(hover:none)]:min-h-10 [@media(hover:none)]:leading-[1.3] [@media(hover:none)]:@min-[158px]:@max-[176px]:text-[11px] [@media(hover:none)]:@min-[158px]:@max-[176px]:px-2 [@media(hover:hover)]:@max-[172px]:py-[6px] [@media(hover:hover)]:@max-[172px]:min-h-[38px] [@media(hover:hover)]:@max-[172px]:leading-[1.15] justify-center items-center self-stretch w-full p-[10px] gap-[10px] bg-cart-button hover:bg-brand-hover text-brand-text text-[12px] font-gotham font-medium transition-colors cursor-pointer"
          style={cartButtonStyle}
        >
          {showCartIcon && <ShoppingCartIcon className="w-[18px] h-[18px] flex-shrink-0 @max-[181px]:hidden" />}
          <span className="text-center text-balance">{isOffer ? CHOOSE_OFFER_LABEL : needsChoice ? choiceLabel : cartAdded ? doneLabel : actionLabel}</span>
        </button>
      )}
      {offerOpen && offerUrl && (
        <OfferPopup offerUrl={offerUrl} title={product.title} onClose={() => setOfferOpen(false)} onCartAdd={handleOfferCartAdd} />
      )}
    </div>
  );
}
